// ============================================
// Calibration Case Detail Card
//
// Expandable card showing full case details:
// biomarkers, blind run, SOAP, comparison, review.
// Conditional rendering, no framer-motion.
// ============================================

import { useState } from 'react';
import { ChevronDown, ChevronRight, CheckCircle, AlertTriangle, XCircle, FlaskConical } from 'lucide-react';
import type { CalibrationCase, ExtractedBiomarker } from '../../../types/labUpload';
import type { DecisionPoint } from '../../../types/clinicalCalibration';

interface CalibrationCaseCardProps {
  calibrationCase: CalibrationCase;
}

function StatusBadge({ status }: { status: string }) {
  const colors: Record<string, string> = {
    imported: 'bg-zinc-100 text-zinc-600',
    phi_checked: 'bg-blue-100 text-blue-700',
    extracted: 'bg-indigo-100 text-indigo-700',
    blind_run_complete: 'bg-purple-100 text-purple-700',
    compared: 'bg-amber-100 text-amber-700',
    reviewed: 'bg-green-100 text-green-700',
    complete: 'bg-green-200 text-green-800',
  };

  return (
    <span className={`text-[13px] font-medium px-2 py-0.5 rounded ${colors[status] || 'bg-zinc-100 text-zinc-600'}`}>
      {status.replace(/_/g, ' ')}
    </span>
  );
}

function AlignmentBadge({ score }: { score: number }) {
  const color = score >= 75 ? 'text-green-700 bg-green-100' :
    score >= 50 ? 'text-amber-700 bg-amber-100' : 'text-red-700 bg-red-100';

  return (
    <span className={`font-mono text-sm font-semibold px-2 py-0.5 rounded ${color}`}>
      {score}%
    </span>
  );
}

function DecisionPointRow({ dp }: { dp: DecisionPoint }) {
  const icon = dp.alignment === 'aligned' ? <CheckCircle size={14} className="text-green-600" /> :
    dp.alignment === 'delta' ? <AlertTriangle size={14} className="text-amber-500" /> :
    <XCircle size={14} className="text-red-500" />;

  return (
    <tr className="border-b border-zinc-100 last:border-0">
      <td className="py-2 pr-3">
        <div className="flex items-center gap-1.5">
          {icon}
          <span className="text-sm text-zinc-800">{dp.area}</span>
        </div>
      </td>
      <td className="py-2 pr-3 text-[13px] text-zinc-600 max-w-[200px] truncate">{dp.drCPosition}</td>
      <td className="py-2 pr-3 text-[13px] text-zinc-600 max-w-[200px] truncate">{dp.medgemmaPosition}</td>
      <td className="py-2 text-[13px] text-zinc-500">{dp.deltaNote || '—'}</td>
    </tr>
  );
}

function ExtractedBiomarkersTable({ biomarkers }: { biomarkers: ExtractedBiomarker[] }) {
  const [open, setOpen] = useState(false);

  return (
    <div>
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1.5 text-[13px] font-semibold text-zinc-500 uppercase tracking-wider mb-2 hover:text-zinc-700 transition-colors"
      >
        <FlaskConical size={14} />
        Extracted Biomarkers ({biomarkers.length})
        {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
      </button>
      {open && (
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="bg-[#18181B] text-white text-[13px]">
                <th className="py-2 px-3 font-medium">Raw Name</th>
                <th className="py-2 px-3 font-medium">Canonical ID</th>
                <th className="py-2 px-3 font-medium">Value</th>
                <th className="py-2 px-3 font-medium">Unit</th>
                <th className="py-2 px-3 font-medium">Confidence</th>
              </tr>
            </thead>
            <tbody>
              {biomarkers.map((bm, i) => (
                <tr
                  key={i}
                  className={`border-b border-zinc-100 last:border-0 ${i % 2 === 1 ? 'bg-zinc-50' : 'bg-white'}`}
                >
                  <td className="py-2 px-3 text-[13px] text-zinc-800">{bm.rawName}</td>
                  <td className="py-2 px-3 text-[13px] font-mono text-zinc-600">{bm.canonicalId ?? '—'}</td>
                  <td className="py-2 px-3 text-[13px] font-mono text-zinc-800">{bm.value ?? '—'}</td>
                  <td className="py-2 px-3 text-[13px] text-zinc-600">{bm.unit}</td>
                  <td className="py-2 px-3 text-[13px] font-mono text-zinc-600">
                    {Math.round(bm.confidence * 100)}%
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export function CalibrationCaseCard({ calibrationCase: c }: CalibrationCaseCardProps) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="bg-white border border-zinc-200 rounded">
      {/* Header row — always visible */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between p-4 text-left hover:bg-zinc-50 transition-colors"
      >
        <div className="flex items-center gap-3">
          {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          <span className="text-sm font-medium text-zinc-800">
            Case #{c.caseId.slice(-6)}
          </span>
          <StatusBadge status={c.status} />
        </div>

        <div className="flex items-center gap-3">
          {c.demographics && (
            <span className="text-[13px] text-zinc-500">
              {c.demographics.sex ?? '?'}, {c.demographics.age ?? '?'}y
            </span>
          )}
          {c.alignmentScore !== undefined && (
            <AlignmentBadge score={c.alignmentScore} />
          )}
          {c.drCReview && (
            <span className={`text-[13px] px-2 py-0.5 rounded ${
              c.drCReview.rating === 'yes' ? 'bg-green-100 text-green-700' :
              c.drCReview.rating === 'mostly' ? 'bg-amber-100 text-amber-700' :
              'bg-red-100 text-red-700'
            }`}>
              {c.drCReview.rating}
            </span>
          )}
        </div>
      </button>

      {/* Expanded content */}
      {expanded && (
        <div className="border-t border-zinc-100 p-4 space-y-4">
          {/* Blind Run Output */}
          {c.blindRunOutput && (
            <div>
              <h4 className="text-[13px] font-semibold text-zinc-500 uppercase tracking-wider mb-1">Model Assessment</h4>
              <p className="text-sm text-zinc-800">{c.blindRunOutput.diagnosis}</p>
              {c.blindRunOutput.recommendedProducts.length > 0 && (
                <p className="text-[13px] text-zinc-500 mt-1">
                  Products: {c.blindRunOutput.recommendedProducts.join(', ')}
                </p>
              )}
              {c.primaryFindings && c.primaryFindings.length > 0 && (
                <div className="mt-1 flex gap-1 flex-wrap">
                  {c.primaryFindings.map((f, i) => (
                    <span key={i} className="text-[13px] bg-indigo-50 text-indigo-700 px-1.5 py-0.5 rounded">
                      {f.rootCause} ({Math.round(f.confidence * 100)}%)
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Dr. C SOAP Assessment */}
          {c.drCAssessment && (
            <div>
              <h4 className="text-[13px] font-semibold text-zinc-500 uppercase tracking-wider mb-1">Dr. C Assessment (SOAP)</h4>
              <p className="text-sm text-zinc-800">{c.drCAssessment.diagnosis}</p>
              {c.drCAssessment.products.length > 0 && (
                <p className="text-[13px] text-zinc-500 mt-1">
                  Products: {c.drCAssessment.products.join(', ')}
                </p>
              )}
            </div>
          )}

          {/* 9-Point Comparison Table */}
          {c.decisionPoints && c.decisionPoints.length > 0 && (
            <div>
              <h4 className="text-[13px] font-semibold text-zinc-500 uppercase tracking-wider mb-2">Comparison (9 Decision Points)</h4>
              <div className="overflow-x-auto">
                <table className="w-full text-left">
                  <thead>
                    <tr className="bg-zinc-900 text-white text-[13px]">
                      <th className="py-2 px-3 font-medium">Area</th>
                      <th className="py-2 px-3 font-medium">Dr. C</th>
                      <th className="py-2 px-3 font-medium">Model</th>
                      <th className="py-2 px-3 font-medium">Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {c.decisionPoints.map((dp, i) => (
                      <DecisionPointRow key={i} dp={dp} />
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Extracted Biomarkers */}
          {c.extractedBiomarkers && c.extractedBiomarkers.length > 0 && (
            <ExtractedBiomarkersTable biomarkers={c.extractedBiomarkers} />
          )}

          {/* Dr. C Review */}
          {c.drCReview && (
            <div className="bg-zinc-50 rounded p-3">
              <h4 className="text-[13px] font-semibold text-zinc-500 uppercase tracking-wider mb-1">Dr. C Review</h4>
              <p className="text-sm text-zinc-800">
                Rating: <strong>{c.drCReview.rating}</strong>
                {c.drCReview.comment && ` — "${c.drCReview.comment}"`}
              </p>
              <p className="text-[13px] text-zinc-500 mt-1">
                {new Date(c.drCReview.reviewedAt).toLocaleDateString()}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
